import React, { useEffect, useRef, useState } from "react";
import { apiClient } from "../lib/api";
import { EmployeeHeader } from "../components/EmployeeHeader";
import { Skeleton } from "../components/ui/skeleton";
import { Building2, Phone, Mail, Lock, Plus } from "lucide-react";
import { toast } from "sonner";

export default function EmployeeProfile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    apiClient.get("/me/employee-profile").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Phase 7 #7 — employee can upload/change their own photo; same
  // base64-in-Mongo `employees.profile_photo` field the admin side edits.
  const onFile = (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Image must be under 3MB"); return; }
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const dataUrl = ev.target.result;
      setUploading(true);
      try {
        await apiClient.put("/me/employee-profile/photo", { profile_photo: dataUrl });
        setData((d) => ({ ...d, profile_photo: dataUrl }));
        toast.success("Photo updated");
      } catch {
        toast.error("Failed to update photo");
      } finally {
        setUploading(false);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  return (
    <>
      <EmployeeHeader title="Profile" subtitle={data?.company_name || ""} />
      <div className="px-4 pt-5 pb-28 space-y-4" data-testid="employee-profile-page">
        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-16 rounded-2xl" />
            <Skeleton className="h-52 rounded-2xl" />
            <Skeleton className="h-40 rounded-2xl" />
          </div>
        ) : (
          <>
            <div className="bg-indigo-600 rounded-2xl p-4 text-white flex items-center gap-2" data-testid="profile-firm-name">
              <Building2 className="w-5 h-5 shrink-0" strokeWidth={2.5} />
              <span className="font-bold text-lg truncate">{data?.company_name || "—"}</span>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col items-center text-center" data-testid="profile-identity-card">
              <div className="relative mb-3">
                {data?.profile_photo ? (
                  <img src={data.profile_photo} alt={data.name} data-testid="profile-photo"
                    className="w-24 h-24 rounded-full object-cover border-4 border-indigo-100" />
                ) : (
                  <div data-testid="profile-photo-fallback" className="w-24 h-24 rounded-full bg-indigo-100 text-indigo-700 grid place-items-center text-3xl font-bold">
                    {(data?.name || "?").charAt(0).toUpperCase()}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="profile-photo-upload-btn"
                  className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white grid place-items-center border-2 border-white shadow disabled:opacity-60"
                  aria-label="Change profile photo"
                >
                  <Plus className="w-4 h-4" strokeWidth={3} />
                </button>
                <input ref={fileRef} type="file" accept="image/*" onChange={onFile} data-testid="profile-photo-input" className="hidden" />
              </div>
              <h2 data-testid="profile-name" className="text-xl font-bold text-slate-900">{data?.name}</h2>
              <p data-testid="profile-position" className="text-sm font-semibold text-indigo-600 mt-0.5">{data?.designation || "—"}</p>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100 overflow-hidden" data-testid="profile-details-card">
              <Row icon={Phone} label="Mobile Number" value={data?.phone || "Not set"} testid="profile-phone" />
              <Row icon={Mail} label="Email ID" value={data?.email || "Not set"} testid="profile-email" />
              <Row icon={Lock} label="Employee ID" value={data?.employee_id || "—"} testid="profile-employee-id" locked />
            </div>

            <p className="text-xs text-slate-400 text-center px-4" data-testid="profile-edit-hint">
              To update your name, position, mobile or email, contact your business owner/admin.
            </p>
          </>
        )}
      </div>
    </>
  );
}

function Row({ icon: Icon, label, value, testid, locked = false }) {
  return (
    <div className="flex items-center gap-3 p-4" data-testid={testid}>
      <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${locked ? "bg-slate-100 text-slate-500" : "bg-indigo-50 text-indigo-600"}`}>
        <Icon className="w-5 h-5" strokeWidth={2.5} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
        <div className="font-semibold text-slate-900 truncate">{value}</div>
      </div>
      {locked && <span data-testid="profile-employee-id-locked-icon"><Lock className="w-3.5 h-3.5 text-slate-300 shrink-0" /></span>}
    </div>
  );
}
